// Dependencies
import { DataRouteMatch, RouteMatch, matchRoutes } from "react-router-dom";

// Project Imports
import router from "./router";

import RouterTags from "../../constants/RouterTags";

const hasTemplateTag = (route: DataRouteMatch | RouteMatch | null | undefined): boolean => {
	const tags = route?.route?.handle?.tags ?? [];
	return Boolean(tags.includes(RouterTags.USES_TEMPLATE) || tags.includes(RouterTags.USES_TEMPLATE_AND_REACT));
};

export const ROUTER_PAGE_TYPE_STORAGE_KEY = "py:last-router-page-type";
export const ROUTER_PAGE_TYPES = {
	react: "react",
	template: "template",
} as const;

export type RouterPageType = (typeof ROUTER_PAGE_TYPES)[keyof typeof ROUTER_PAGE_TYPES];

export const deriveTransitionTypeFromPageTypes = (
	persistedPageType: RouterPageType | null,
	currentPageType: RouterPageType | null
): PageTransitionType | null => {
	if (!persistedPageType || !currentPageType) {
		return null;
	}

	if (persistedPageType === ROUTER_PAGE_TYPES.template && currentPageType === ROUTER_PAGE_TYPES.react) {
		return "TEMPLATE_TO_REACT";
	}

	if (persistedPageType === ROUTER_PAGE_TYPES.react && currentPageType === ROUTER_PAGE_TYPES.template) {
		return "REACT_TO_TEMPLATE";
	}

	return null;
};

export const selectActiveRoute = () => {
	const matches = matchRoutes(router.routes, window.location);
	const routeMatch = matches?.[matches.length - 1];

	return routeMatch;
};

export const translatePathToName = (path: string) => {
	return path.replace(/\//g, "_").trim().replace(/^_/, "");
};

export const selectActiveNavigationItem = () => {
	const routeMatch = selectActiveRoute();
	if (routeMatch) {
		return routeMatch.route?.handle?.activeNavigation ?? null;
	} else {
		return null;
	}
};

/**
 * Given a matched route, it generates a name. First it uses the legacyPageName
 * property if it exists. If it doesn't, it uses the path and translates it to
 * a name.
 * Example: /admin/roles -> admin_roles
 */
export const selectMatchedRouteName = (matchedRoute: RouteMatch): string => {
	const pagePath = matchedRoute?.route?.path ?? "__none__";
	const legacyPageName = matchedRoute?.route?.handle?.legacyPageName ?? null;
	const pageName = legacyPageName ? legacyPageName : translatePathToName(pagePath);

	return pageName;
};

export const selectCurrentPageName = () => {
	const routeMatch = selectActiveRoute();
	if (routeMatch) {
		return selectMatchedRouteName(routeMatch);
	} else {
		return null;
	}
};

export function parseQueryString(querystr) {
	const query = {};
	const a = (querystr[0] === "?" ? querystr.substr(1) : querystr).split("&");

	for (let i = 0; i < a.length; i += 1) {
		const b = a[i].split("=");

		if (decodeURIComponent(b[0]) === "") {
			continue;
		}

		query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || "");
	}

	return query;
}

/**
 * This object is used to hide and show the tornado and react elements.
 * It is used to prevent the react application from being displayed while
 * the tornado application OR the react application is loading or transitioning is loading.
 */
export const ContentVisibility = {
	_hideTornadoElement() {
		const tornado = document.querySelector(".tornado-wrapper") as HTMLElement;
		if (tornado) tornado.style.display = "none";
	},

	_showTornadoElement() {
		const tornado = document.querySelector(".tornado-wrapper") as HTMLElement;
		if (tornado) tornado.style.display = "";
	},

	_hideReactElement() {
		const react = document.querySelector(".react-application-body") as HTMLElement;
		if (react) react.style.display = "none";
	},

	_showReactElement() {
		const react = document.querySelector(".react-application-body") as HTMLElement;
		if (react) react.style.display = "";
	},

	hideAll() {
		this._hideTornadoElement();
		this._hideReactElement();
	},

	showOnlyTornado() {
		this._showTornadoElement();
		this._hideReactElement();
	},

	showOnlyReact() {
		this._hideTornadoElement();
		this._showReactElement();
	},

	showAll() {
		this._showTornadoElement();
		this._showReactElement();
	},
};

export type PageTransitionType =
	| "INITIAL_MOUNT"
	| "NO_CHANGE"
	| "REACT_TO_TEMPLATE"
	| "TEMPLATE_TO_REACT"
	| "REACT_TO_REACT"
	| "TEMPLATE_TO_TEMPLATE";

export function detectPageTypeTransition(
	previousRoute: DataRouteMatch | null,
	currentRoute: DataRouteMatch | null
): PageTransitionType {
	// Initial page load
	if (previousRoute === null) {
		return "INITIAL_MOUNT";
	}

	if (previousRoute.pathname === currentRoute?.pathname) {
		return "NO_CHANGE";
	}

	const wasTemplatePage = hasTemplateTag(previousRoute);
	const isTemplatePage = hasTemplateTag(currentRoute);

	// Determine transition type
	if (!wasTemplatePage && isTemplatePage) return "REACT_TO_TEMPLATE";
	if (wasTemplatePage && !isTemplatePage) return "TEMPLATE_TO_REACT";
	if (wasTemplatePage && isTemplatePage) return "TEMPLATE_TO_TEMPLATE";
	return "REACT_TO_REACT";
}
